import { loadCollectionRecords, loadRecordsByEquality } from './firestoreQueryHelpers';
import { normalizeDrillTemplateAccess } from './drillTemplateAccess';

function dedupeById(items = []) {
  const seen = new Set();
  return (items || []).filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export async function loadInstructorOwnedDrillTemplates(db, currentUserId = '', currentUserOrgId = '') {
  if (!currentUserId) return [];

  const records = await loadRecordsByEquality(db, {
    collectionName: 'drillTemplates',
    field: 'createdBy',
    value: currentUserId,
  });

  return records.map((template) => normalizeDrillTemplateAccess(template, currentUserOrgId));
}

export async function loadAssignedDrillTemplates(db, currentUserId = '', currentUserOrgId = '') {
  if (!currentUserId) return [];

  const records = await loadRecordsByEquality(db, {
    collectionName: 'drillTemplates',
    field: 'assignedUserIds',
    operator: 'array-contains',
    value: currentUserId,
  });

  return records.map((template) => normalizeDrillTemplateAccess(template, currentUserOrgId));
}

export async function loadOrganizationOwnedDrillTemplates(db, organizationId = '') {
  if (!organizationId) return [];

  const [organizationIdRecords, legacyOrgRecords] = await Promise.all([
    loadRecordsByEquality(db, {
      collectionName: 'drillTemplates',
      field: 'organizationId',
      value: organizationId,
    }),
    loadRecordsByEquality(db, {
      collectionName: 'drillTemplates',
      field: 'orgId',
      value: organizationId,
    }),
  ]);

  const records = dedupeById([
    ...organizationIdRecords,
    ...legacyOrgRecords,
  ]);

  return records.map((template) => normalizeDrillTemplateAccess(template, organizationId));
}

export async function loadPlatformDrillTemplates(db, currentUserOrgId = '') {
  const records = await loadCollectionRecords(db, 'drillTemplates');

  return records
    .filter((template) => template.ownerType === 'platform' || template.visibility === 'platform')
    .map((template) => normalizeDrillTemplateAccess(template, currentUserOrgId));
}

export async function loadScopedDrillTemplates(
  db,
  {
    currentUserId = '',
    organizationIds = [],
    currentUserOrgId = '',
    includePlatform = true,
  } = {}
) {
  const [mine, assigned, ...organizationResults] = await Promise.all([
    loadInstructorOwnedDrillTemplates(db, currentUserId, currentUserOrgId),
    loadAssignedDrillTemplates(db, currentUserId, currentUserOrgId),
    ...(organizationIds || []).map((organizationId) =>
      loadOrganizationOwnedDrillTemplates(db, organizationId)
    ),
    ...(includePlatform ? [loadPlatformDrillTemplates(db, currentUserOrgId)] : []),
  ]);

  const platformResults = includePlatform
    ? organizationResults.pop() || []
    : [];

  return dedupeById([
    ...(mine || []),
    ...(assigned || []),
    ...organizationResults.flat(),
    ...(platformResults || []),
  ]);
}
